import { ComposerPrimitive } from '@assistant-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@uconnect/assets/lib/utils';
import Icon from '@uconnect/assets/components/ui/icon';
import { Button } from '@uconnect/assets/components/ui/button';

// Optical alignment nudge for the send button's icon, keyed by Icon component name (icon.tsx). Purely a
// rendering detail of each icon's glyph shape in this button — unrelated to the `ai_search_fab_icon` WP
// setting, so it stays here rather than in SearchAgentCustomization.php.
const STUDENT_SIDEBAR_ICON_OFFSETS: Record<string, string> = {
	aiSearch: '-translate-y-[1px] translate-x-[1px]',
};

interface ComposerProps {
	context?: string;
	icon?: string;
	isExpanded?: boolean;
	chatDisabled?: boolean;
	isResponding?: boolean;
	onStop?: () => void;
	showStopButtonWhileResponding?: boolean;
	stopIconClassName?: string;
	prefillText?: string;
	prefillRequestId?: number;
	containerMaxWidthClasses?: string;
	value?: string;
	onValueChange?: ( value: string ) => void;
	onSubmit?: () => void;
	submitDisabled?: boolean;
	ariaLabel?: string;
	placeholder?: string;
}

export default function Composer( {
	context,
	icon,
	isExpanded = false,
	chatDisabled = false,
	isResponding = false,
	onStop,
	showStopButtonWhileResponding = true,
	stopIconClassName,
	prefillText = '',
	prefillRequestId = 0,
	containerMaxWidthClasses = null,
	value,
	onValueChange,
	onSubmit,
	submitDisabled = false,
	ariaLabel = 'Enter a prompt',
	placeholder: placeholderOverride,
}: ComposerProps ) {
	let iconName: string;
	let iconClassName: string | undefined;
	let placeholderText: string;
	let wrapperClassName: string;
	let rootClassName: string;
	let textSizeClassName: string;

	switch ( context ) {
		case 'adminSidebar':
			// Not "aiSearch" — this composer sends a chat message, it isn't a
			// search box, and "sparkle" is already this UI's icon for
			// AI-suggested tags/content (CandidateCard, InlineTagEditor, etc.),
			// so reusing it here would misread as "this message is AI-tagged."
			// A plain forward chevron is the unambiguous submit affordance.
			iconName = 'doubleChevronRight';
			iconClassName = '';
			placeholderText = 'Ask the Admin Agent…';
			stopIconClassName = '';
			// No horizontal padding here (only vertical) — the transcript viewport
			// and action-footer rows apply containerMaxWidthClasses directly with
			// no padding layer above it, so this must match or its w-full/max-w-3xl
			// computes against a narrower parent than theirs, making the composer's
			// bordered box narrower than the rest of the container at every width.
			wrapperClassName = 'pb-5! pt-1!';
			rootClassName = 'bg-transparent!';
			// Match the "Try asking" suggestion buttons' text size for visual consistency.
			textSizeClassName = 'text-sm!';
			break;
		case 'studentSidebar':
			iconName = icon || 'aiSearch';
			iconClassName = STUDENT_SIDEBAR_ICON_OFFSETS[iconName] ?? '';
			placeholderText = 'Search Career Content';
			stopIconClassName = '';
			wrapperClassName = 'px-5! pb-5! pt-1!';
			rootClassName = 'bg-composer-background!';
			textSizeClassName = 'text-base!';
			break;
		default:
			iconName = 'send';
			iconClassName = '-translate-y-[1px]';
			stopIconClassName = '';
			placeholderText = 'Type your message…';
			wrapperClassName = 'p-3! border-t! border-border!';
			rootClassName = 'bg-composer-background!';
			textSizeClassName = 'text-base!';
			break;
	}

	const resolvedPlaceholder = placeholderOverride || placeholderText;

	const isControlled = typeof onValueChange === 'function' && typeof onSubmit === 'function';
	const showStopAction = isResponding && showStopButtonWhileResponding;
	const actionLabel = showStopAction ? 'Stop response' : 'Send message';
	const actionIconName = showStopAction ? 'xmark' : iconName;
	const actionIconClassName = showStopAction ? ( stopIconClassName ?? iconClassName ) : iconClassName;
	const [ liveAnnouncement, setLiveAnnouncement ] = useState( '' );
	const wasRespondingRef = useRef( isResponding );
	const rootRef = useRef< HTMLDivElement | null >( null );
	const controlledInputRef = useRef< HTMLTextAreaElement | null >( null );
	const controlledPlaceholderRef = useRef< HTMLTextAreaElement | null >( null );

	useEffect( () => {
		if ( isResponding && ! wasRespondingRef.current ) {
			setLiveAnnouncement( 'Assistant is responding. Stop button is available.' );
		} else if ( ! isResponding && wasRespondingRef.current ) {
			setLiveAnnouncement( '' );
			window.requestAnimationFrame( () => {
				const input = isControlled
					? controlledInputRef.current
					: rootRef.current?.querySelector( 'textarea[name="message-input"]' ) as HTMLTextAreaElement | null;

				if ( input && ! chatDisabled ) {
					input.focus();
				}
			} );
		}

		wasRespondingRef.current = isResponding;
	}, [ isResponding, isControlled, chatDisabled ] );

	useEffect( () => {
		if ( isControlled ) {
			return;
		}

		if ( ! prefillRequestId || ! rootRef.current || ! prefillText ) {
			return;
		}

		const input = rootRef.current.querySelector( 'textarea[name="message-input"]' ) as HTMLTextAreaElement | null;
		if ( ! input ) {
			return;
		}

		// Use the native prototype setter to bypass React's internal value tracking,
		// so React treats this as an external change and updates its state.
		const nativeSetter = Object.getOwnPropertyDescriptor( window.HTMLTextAreaElement.prototype, 'value' )?.set;
		nativeSetter?.call( input, prefillText );
		input.dispatchEvent( new Event( 'input', { bubbles: true } ) );
		input.focus();
	}, [ prefillRequestId, prefillText ] );

	useLayoutEffect( () => {
		if ( ! isControlled ) {
			return;
		}

		const input = controlledInputRef.current;
		const placeholderSizer = controlledPlaceholderRef.current;
		if ( ! input || ! placeholderSizer ) {
			return;
		}

		placeholderSizer.value = value?.trim().length ? value : resolvedPlaceholder;
		input.style.height = 'auto';
		const nextHeight = Math.max( input.scrollHeight, placeholderSizer.scrollHeight );
		input.style.height = `${ nextHeight }px`;
	}, [ isControlled, value, resolvedPlaceholder ] );

	const wrapperClasses = cn(
		'group bg-white! flex w-full items-end gap-2 border! border-border! rounded pr-1.5!',
		'has-[textarea:focus]:outline has-[textarea:focus]:outline-1 has-[textarea:focus]:outline-ring',
		'has-[textarea:focus]:border-ring!',
		( chatDisabled || isResponding ) && 'cursor-not-allowed bg-[#f1f1f1]!',
		isExpanded && containerMaxWidthClasses
	);

	const inputClasses = cn(
		'agent-composer-input mb-0! block max-h-[7.5em]! min-w-0! w-full! flex-1! resize-none border-0! p-3!',
		textSizeClassName,
		'focus:outline-none! focus:shadow-none!',
		( chatDisabled || isResponding ) && 'cursor-not-allowed bg-[#f1f1f1]! opacity-70!'
	);

	const sendButtonClasses = cn(
		'default self-center rounded! flex-grow-0 flex-shrink-0 p-0! mb-0!',
		! showStopAction && 'agent-composer-send'
	);

	if ( isControlled ) {
		return (
			<div
				ref={ rootRef }
				className={ cn(
					'agent-composer relative',
					rootClassName,
					isExpanded && 'flex justify-center',
					wrapperClassName
				) }
			>
				<div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
					{ liveAnnouncement }
				</div>
				<div className={ wrapperClasses }>
					<textarea
						ref={ controlledInputRef }
						name="message-input"
						value={ value ?? '' }
						onChange={ ( event ) => onValueChange( event.target.value ) }
						onKeyDown={ ( event ) => {
							if ( event.key === 'Enter' && ! event.shiftKey ) {
								event.preventDefault();
								if ( ! chatDisabled && ! isResponding && ! submitDisabled ) {
									onSubmit();
								}
							}
						} }
						className={ inputClasses }
						placeholder={ resolvedPlaceholder }
						aria-label={ ariaLabel }
						disabled={ chatDisabled || isResponding }
						rows={ 1 }
					/>
					<textarea
						ref={ controlledPlaceholderRef }
						aria-hidden="true"
						tabIndex={ -1 }
						readOnly
						className={ cn(
							inputClasses,
							'absolute left-0 top-0 -z-10 h-0! min-h-0! overflow-hidden! opacity-0 pointer-events-none'
						) }
						rows={ 1 }
					/>
					<Button
						type="button"
						size="icon"
						variant="default"
						className={ sendButtonClasses }
						onClick={ onSubmit }
						disabled={ chatDisabled || isResponding || submitDisabled }
						aria-label={ actionLabel }
						title={ actionLabel }
					>
						<Icon name={ actionIconName } className={ cn( 'text-white! size-4.5', actionIconClassName ) } />
					</Button>
				</div>
			</div>
		);
	}

	return (
		<ComposerPrimitive.Root
			ref={ rootRef }
			className={ cn(
				'agent-composer relative',
				rootClassName,
				isExpanded && 'flex justify-center',
				wrapperClassName
			) }
		>
			<div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
				{ liveAnnouncement }
			</div>
			<div className={ wrapperClasses }>
				<ComposerPrimitive.Input
					name="message-input"
					className={ inputClasses }
					placeholder={ resolvedPlaceholder }
					aria-label={ ariaLabel }
					disabled={ chatDisabled || isResponding }
				/>
				<Button
					asChild={ ! showStopAction }
					type={ showStopAction ? 'button' : 'submit' }
					size="icon"
					variant="default"
					className={ sendButtonClasses }
					onClick={ showStopAction ? onStop : undefined }
					disabled={ ! showStopAction ? chatDisabled : false }
					data-composer-send={ ! showStopAction ? 'true' : undefined }
					aria-label={ actionLabel }
					title={ actionLabel }
				>
					{ showStopAction ? (
						<Icon name={ actionIconName } className={ cn( 'text-white! size-4.5', actionIconClassName ) } />
					) : (
						<ComposerPrimitive.Send>
							<Icon name={ actionIconName } className={ cn( 'text-white! size-4.5', actionIconClassName ) } />
						</ComposerPrimitive.Send>
					) }
				</Button>
			</div>
		</ComposerPrimitive.Root>
	);
}
